import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material"
import { Search, Map as MapIcon, List as ListIcon, MyLocation } from "@mui/icons-material"
import BathroomCard from "../components/BathroomCard"
import BathroomMap from "../components/BathroomMap"
import { fetchAllBathrooms, fetchNearbyBathrooms } from "../services/bathroomService"
import { getCurrentLocation } from "../utils/distanceUtils"

// Skeleton Card Component
const BathroomCardSkeleton = () => (
  <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <CardContent sx={{ flexGrow: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="rectangular" width={60} height={24} />
      </Box>
      <Skeleton variant="rectangular" width="100%" height={150} sx={{ mb: 2, borderRadius: "8px" }} />
      <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
      <Box display="flex" gap={0.5} mb={1}>
        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "12px" }} />
        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "12px" }} />
      </Box>
      <Skeleton variant="text" width="90%" height={20} />
    </CardContent>
    <Box sx={{ p: 2, pt: 0 }}>
      <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: "4px" }} />
    </Box>
  </Card>
)

const CACHE_KEY = "crapper_mapper_bathrooms_cache"
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

const HomePage = () => {
  const navigate = useNavigate()

  // Load cached data helper
  const loadFromCache = () => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY)
      if (cached) {
        const { data, timestamp, location, distance } = JSON.parse(cached)
        const now = Date.now()

        // Check if cache is still valid
        if (now - timestamp < CACHE_DURATION) {
          return { data, location, distance }
        }
      }
    } catch (err) {
      console.error("Error loading cache:", err)
    }
    return null
  }

  // Check cache immediately to set initial state
  const initialCache = loadFromCache()
  const [bathrooms, setBathrooms] = useState(initialCache?.data || [])
  const [filteredBathrooms, setFilteredBathrooms] = useState(initialCache?.data || [])
  const [loading, setLoading] = useState(!initialCache) // Only show loading if no cache
  const [error, setError] = useState(null)
  const [userLocation, setUserLocation] = useState(initialCache?.location || null)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeTab, setActiveTab] = useState(0) // 0 = List, 1 = Map
  const [maxDistance, setMaxDistance] = useState(initialCache?.distance || 5)

  // Save to cache
  const saveToCache = (data, location, distance) => {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data,
          location,
          distance,
          timestamp: Date.now(),
        }),
      )
    } catch (err) {
      console.error("Error saving cache:", err)
    }
  }

  // Re-fetch when maxDistance changes
  useEffect(() => {
    if (!userLocation) return

    const refetchWithNewDistance = async () => {
      setLoading(true)
      try {
        const nearbyBathrooms = await fetchNearbyBathrooms(userLocation.latitude, userLocation.longitude, maxDistance)
        setBathrooms(nearbyBathrooms)
        saveToCache(nearbyBathrooms, userLocation, maxDistance)
      } catch (err) {
        console.error("Error re-fetching bathrooms:", err)
      } finally {
        setLoading(false)
      }
    }

    refetchWithNewDistance()
  }, [maxDistance, userLocation])

  useEffect(() => {
    const initializePage = async () => {
      // If we already loaded from cache on mount, skip fetching
      if (initialCache && initialCache.distance === maxDistance) {
        console.log("✅ Using cached bathrooms from initial load - NO FETCH")
        return
      }

      console.log("🔄 No cache found - fetching from Firebase")
      setLoading(true)
      try {
        // Try to get user location first
        const location = await getCurrentLocation()
        setUserLocation(location)

        // Load bathrooms based on location
        if (location) {
          console.log("📍 Fetching nearby bathrooms with location")
          const nearbyBathrooms = await fetchNearbyBathrooms(location.latitude, location.longitude, maxDistance)
          setBathrooms(nearbyBathrooms)
          saveToCache(nearbyBathrooms, location, maxDistance)
        } else {
          console.log("🌍 Fetching all bathrooms without location")
          const allBathrooms = await fetchAllBathrooms()
          setBathrooms(allBathrooms)
          saveToCache(allBathrooms, null, maxDistance)
        }
        setError(null)
      } catch (err) {
        console.log("Could not get user location, loading all bathrooms:", err.message)
        // Fallback: load all bathrooms without location
        try {
          const allBathrooms = await fetchAllBathrooms()
          setBathrooms(allBathrooms)
          saveToCache(allBathrooms, null, maxDistance)
          setError(null)
        } catch (loadErr) {
          setError("Failed to load bathrooms. Please try again.")
          console.error("Error loading bathrooms:", loadErr)
        }
      } finally {
        setLoading(false)
      }
    }

    initializePage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBathrooms(bathrooms)
      return
    }

    const filtered = bathrooms.filter(bathroom => {
      const searchLower = searchTerm.toLowerCase()

      // Handle amenities as either array or object
      let amenitiesMatch = false
      if (bathroom.amenities) {
        if (Array.isArray(bathroom.amenities)) {
          amenitiesMatch = bathroom.amenities.some(amenity => amenity.toLowerCase().includes(searchLower))
        } else if (typeof bathroom.amenities === "object") {
          amenitiesMatch = Object.keys(bathroom.amenities).some(key => key.toLowerCase().includes(searchLower))
        }
      }

      return (
        (bathroom.name && bathroom.name.toLowerCase().includes(searchLower)) ||
        (bathroom.address && bathroom.address.toLowerCase().includes(searchLower)) ||
        amenitiesMatch
      )
    })

    setFilteredBathrooms(filtered)
  }, [bathrooms, searchTerm])

  const handleBathroomClick = bathroom => {
    // Use locationId/bathroomId format for new structure
    const bathroomPath = bathroom.locationId ? `${bathroom.locationId}/${bathroom.id}` : bathroom.id
    navigate(`/bathroom/${bathroomPath}`)
  }

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue)
  }

  const handleLocationClick = async () => {
    setLoading(true)
    try {
      const location = await getCurrentLocation()
      setUserLocation(location)

      if (location) {
        const nearbyBathrooms = await fetchNearbyBathrooms(location.latitude, location.longitude, maxDistance)
        setBathrooms(nearbyBathrooms)
        saveToCache(nearbyBathrooms, location, maxDistance)
        setError(null)
      }
    } catch (err) {
      setError("Could not get your location. Please enable location services.")
      console.error("Location error:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleDistanceChange = async event => {
    const newDistance = event.target.value
    setMaxDistance(newDistance)

    // Reload bathrooms with new distance if we have user location
    if (userLocation) {
      setLoading(true)
      try {
        const nearbyBathrooms = await fetchNearbyBathrooms(userLocation.latitude, userLocation.longitude, newDistance)
        setBathrooms(nearbyBathrooms)
        saveToCache(nearbyBathrooms, userLocation, newDistance)
        setError(null)
      } catch (err) {
        setError("Failed to load bathrooms with new distance.")
        console.error("Error loading bathrooms:", err)
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          🚽 TNKL
        </Typography>
        <Typography variant="h6" color="text.secondary" align="center">
          Find and review public bathrooms near you
        </Typography>

        {/* App Store Badge */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <a href="https://apps.apple.com/app/id6744712179" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block" }}>
            <img
              src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1735344000"
              alt="Download on the App Store"
              style={{ height: "60px", borderRadius: "8px" }}
            />
          </a>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Bar and Distance Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={userLocation ? 8 : 12}>
              <TextField
                fullWidth
                placeholder="Search bathrooms by name, address, or amenities..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {userLocation && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="distance-select-label">Distance</InputLabel>
                  <Select labelId="distance-select-label" id="distance-select" value={maxDistance} label="Distance" onChange={handleDistanceChange}>
                    <MenuItem value={5}>5 miles</MenuItem>
                    <MenuItem value={10}>10 miles</MenuItem>
                    <MenuItem value={25}>25 miles</MenuItem>
                    <MenuItem value={50}>50 miles</MenuItem>
                    <MenuItem value={100}>100 miles</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Stats */}
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h4" color="primary">
                  {filteredBathrooms.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bathrooms Found
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h4" color="primary">
                  {userLocation ? "📍" : "❌"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Location {userLocation ? "Enabled" : "Disabled"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card style={{ padding: 12, justifyContent: "center", alignItems: "center" }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Button variant="outlined" startIcon={<MyLocation />} onClick={handleLocationClick} disabled={loading} fullWidth>
                  {userLocation ? "Update Location" : "Get Location"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* View Toggle */}
      <Box mb={3}>
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          <Tab icon={<ListIcon />} label="List View" />
          <Tab icon={<MapIcon />} label="Map View" />
        </Tabs>
      </Box>

      {/* Content */}
      {activeTab === 0 ? (
        // List View
        <Grid container spacing={3}>
          {loading ? (
            // Show skeleton cards while loading
            <>
              {[1, 2, 3, 4, 5, 6].map(index => (
                <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                  <BathroomCardSkeleton />
                </Grid>
              ))}
            </>
          ) : (
            <>
              {filteredBathrooms.map(bathroom => (
                <Grid item xs={12} sm={6} md={4} key={bathroom.id}>
                  <BathroomCard bathroom={bathroom} onViewDetails={handleBathroomClick} showDistance={!!userLocation} />
                </Grid>
              ))}
              {filteredBathrooms.length === 0 && bathrooms.length === 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent sx={{ textAlign: "center", py: 6 }}>
                      <Typography variant="h6" color="text.secondary">
                        No bathrooms found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search or location settings
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </>
          )}
        </Grid>
      ) : (
        // Map View
        <Card>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ height: "600px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "grey.100" }}>
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress size={60} />
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    Loading map...
                  </Typography>
                </Box>
              </Box>
            ) : (
              <BathroomMap bathrooms={filteredBathrooms} userLocation={userLocation} onBathroomClick={handleBathroomClick} height="600px" />
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  )
}

export default HomePage
